import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Shield, Clock, MapPin, Car, ChevronRight, AlertTriangle, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { publicApi } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { BRAND } from "@/lib/brand";

const money = (n) => (n == null ? "—" : `£${Number(n).toLocaleString("en-GB", { maximumFractionDigits: 2 })}`);

export default function QuoteView() {
  const { ref } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [terms, setTerms] = useState(false);
  const [paymentMode, setPaymentMode] = useState("deposit"); // "deposit" | "full"
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await publicApi.get(`/quote/${encodeURIComponent(ref)}`);
        if (cancelled) return;
        setQuote(data);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "Quote not found.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ref]);

  async function secureBooking() {
    if (!terms) {
      toast.error("Please accept the Terms & Conditions to continue.");
      return;
    }
    setRedirecting(true);
    try {
      const { data } = await publicApi.post(`/quote/${encodeURIComponent(ref)}/checkout`, {
        origin_url: window.location.origin,
        terms_accepted: true,
        payment_mode: paymentMode,
      });
      window.location.href = data.url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start checkout.");
      setRedirecting(false);
    }
  }

  return (
    <main data-testid="quote-view-page" className="bg-[#050505] text-white min-h-screen">
      <Header />
      <div className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          {loading ? (
            <div className="flex items-center gap-3 text-zinc-400 py-24 justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-[#E60000]" /> Loading your quote…
            </div>
          ) : error ? (
            <ErrorCard message={error} />
          ) : quote ? (
            <QuoteBody
              quote={quote}
              terms={terms}
              setTerms={setTerms}
              paymentMode={paymentMode}
              setPaymentMode={setPaymentMode}
              redirecting={redirecting}
              onPay={secureBooking}
            />
          ) : null}
        </div>
      </div>
      <Footer />
    </main>
  );
}

function QuoteBody({ quote, terms, setTerms, paymentMode, setPaymentMode, redirecting, onPay }) {
  const paid = quote.payment_status === "paid" || quote.payment_status === "paid_in_full";
  const expired = quote.expired;
  const outstandingFull = Math.max(0, Number(quote.transport_price || 0) - Number(quote.deposit_paid || 0));
  const chargeNow = paymentMode === "full" ? outstandingFull : Number(quote.deposit_due || 0);

  return (
    <>
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-4">
        Your MHJ Prestige Transport Quote
      </div>
      <h1 className="font-display font-extrabold text-white text-4xl md:text-6xl leading-[1.0] tracking-tight">
        Booking
        <br />
        <span className="text-stroke">{quote.booking_ref}</span>
      </h1>

      {expired && !paid && (
        <div className="mt-8 border border-yellow-500/40 bg-yellow-500/5 p-5 flex items-start gap-3" data-testid="quote-expired">
          <AlertTriangle className="w-5 h-5 shrink-0 text-yellow-400 mt-0.5" />
          <div>
            <div className="text-yellow-300 font-bold text-sm uppercase tracking-[0.2em]">Quote expired</div>
            <p className="text-yellow-100/80 text-sm mt-1">
              This quote has expired. Please contact MHJ Prestige Transport for an updated price.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <a href={`tel:${BRAND.phoneRaw}`} className="btn-primary"><Phone className="w-4 h-4" />Call {BRAND.phone}</a>
              <a href={`mailto:${BRAND.email}?subject=Booking ${quote.booking_ref}`} className="btn-ghost"><Mail className="w-4 h-4" />Email us</a>
            </div>
          </div>
        </div>
      )}

      {paid && (
        <div className="mt-8 border border-emerald-500/30 bg-emerald-500/5 p-5 text-emerald-200" data-testid="quote-paid">
          <div className="text-xs font-bold uppercase tracking-[0.2em]">
            {quote.payment_status === "paid_in_full" ? "Paid in full" : "Deposit received"}
          </div>
          <p className="text-sm mt-1">
            Thank you — we&apos;ve confirmed your payment. Our team will be in touch to confirm collection.
          </p>
        </div>
      )}

      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mt-8 border border-white/10 bg-[#0A0A0A] p-6 md:p-8 space-y-6"
      >
        <div className="grid sm:grid-cols-2 gap-4">
          <Cell label="Vehicle" icon={<Car />}>
            {[quote.vehicle_year, quote.vehicle_make, quote.vehicle_model].filter(Boolean).join(" ") || "—"}
            {quote.vehicle_registration && <div className="text-xs text-zinc-500 mt-1">Reg: {quote.vehicle_registration}</div>}
          </Cell>
          <Cell label="Requested collection" icon={<Clock />}>
            {quote.requested_collection_date || "—"}
            {quote.requested_collection_time && quote.requested_collection_time !== "flexible" && (
              <div className="text-xs text-zinc-500 mt-1">{quote.requested_collection_time}</div>
            )}
          </Cell>
          <Cell label="Collection" icon={<MapPin />}>{quote.pickup_location}</Cell>
          <Cell label="Delivery" icon={<MapPin />}>{quote.dropoff_location}</Cell>
        </div>

        <div className="pt-6 border-t border-white/10 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="quote-pricing">
          <PriceCell label="Agreed transport price" value={money(quote.transport_price)} big />
          <PriceCell label="Deposit due today" value={money(quote.deposit_due)} accent />
          <PriceCell label="Balance remaining" value={money(quote.balance_remaining)} muted />
        </div>

        {quote.quote_expires_at && !paid && !expired && (
          <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-bold flex items-center gap-2" data-testid="quote-expiry">
            <Clock className="w-3.5 h-3.5 text-[#E60000]" />
            Quote valid until{" "}
            {new Date(quote.quote_expires_at).toLocaleString("en-GB", {
              timeZone: "Europe/London",
              dateStyle: "medium",
              timeStyle: "short",
            })}
          </div>
        )}
      </motion.section>

      {!paid && !expired && (
        <div className="mt-8 border border-white/10 bg-[#0A0A0A] p-6 md:p-8 space-y-6">
          <div data-testid="quote-payment-mode" className="space-y-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
              Choose how you&rsquo;d like to pay
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="quote-mode-deposit"
                onClick={() => setPaymentMode("deposit")}
                className={`text-left border p-4 transition-colors ${
                  paymentMode === "deposit"
                    ? "border-[#E60000] bg-[#E60000]/5"
                    : "border-white/15 hover:border-white/40"
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
                    Book Now &mdash; Pay Deposit
                  </div>
                  {paymentMode === "deposit" && <div className="w-2 h-2 rounded-full bg-[#E60000]" />}
                </div>
                <div className="mt-1 text-white font-bold text-sm">Card · Apple Pay · Google Pay</div>
                <div className="mt-2 font-display text-2xl font-extrabold text-white">{money(quote.deposit_due)}</div>
                <div className="text-[11px] text-zinc-500 mt-1">
                  Balance {money(quote.balance_remaining)} due on delivery
                </div>
              </button>
              <button
                type="button"
                data-testid="quote-mode-full"
                onClick={() => setPaymentMode("full")}
                className={`text-left border p-4 transition-colors ${
                  paymentMode === "full"
                    ? "border-[#E60000] bg-[#E60000]/5"
                    : "border-white/15 hover:border-white/40"
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
                    Pay Full Amount &mdash; Card or Klarna
                  </div>
                  {paymentMode === "full" && <div className="w-2 h-2 rounded-full bg-[#E60000]" />}
                </div>
                <div className="mt-1 text-white font-bold text-sm">Card · Apple Pay · Google Pay · Klarna</div>
                <div className="mt-2 font-display text-2xl font-extrabold text-white">{money(outstandingFull)}</div>
                <div className="text-[11px] text-zinc-500 mt-1">Nothing more to pay on delivery</div>
                <KlarnaHint amount={outstandingFull} />
              </button>
            </div>
          </div>

          <div className="flex items-start gap-3 pt-2 border-t border-white/10">
            <input
              id="terms"
              data-testid="quote-terms-checkbox"
              type="checkbox"
              checked={terms}
              onChange={(e) => setTerms(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#E60000] cursor-pointer"
            />
            <label htmlFor="terms" className="text-sm text-zinc-300 cursor-pointer">
              I have read and accept the{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">
                Terms &amp; Conditions
              </a>{" "}
              and{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">
                Privacy Policy
              </a>
              .
            </label>
          </div>

          <button
            data-testid="quote-secure-btn"
            onClick={onPay}
            disabled={!terms || redirecting}
            className="w-full inline-flex items-center justify-center gap-3 bg-[#E60000] hover:bg-[#FF1A1A] px-6 py-5 text-sm tracking-[0.3em] uppercase font-bold text-white transition-colors disabled:opacity-40"
          >
            {redirecting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Shield className="w-5 h-5" />}
            {paymentMode === "full"
              ? `Pay ${money(chargeNow)} in full`
              : `Secure booking · ${money(chargeNow)} deposit`}
            <ChevronRight className="w-5 h-5" />
          </button>

          <div className="text-center text-[10px] tracking-[0.3em] uppercase text-zinc-500">
            Secure payment via Stripe · No stored card details
          </div>
        </div>
      )}
    </>
  );
}

function ErrorCard({ message }) {
  return (
    <div data-testid="quote-error" className="border border-white/10 bg-[#0A0A0A] p-10 text-center max-w-lg mx-auto mt-16">
      <AlertTriangle className="w-10 h-10 mx-auto text-[#E60000]" />
      <h2 className="font-display text-2xl font-bold mt-4">Quote not available</h2>
      <p className="text-zinc-400 text-sm mt-3">{message}</p>
      <div className="mt-6 flex justify-center gap-3">
        <a href={`tel:${BRAND.phoneRaw}`} className="btn-primary"><Phone className="w-4 h-4" /> Call {BRAND.phone}</a>
        <a href="/" className="btn-ghost">Back to site</a>
      </div>
    </div>
  );
}

function Cell({ label, icon, children }) {
  return (
    <div className="border border-white/10 bg-[#050505] p-4">
      <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
        {icon && <span className="text-[#E60000]">{icon}</span>}
        {label}
      </div>
      <div className="text-white text-sm mt-2 break-words">{children ?? "—"}</div>
    </div>
  );
}

function PriceCell({ label, value, big, accent, muted }) {
  return (
    <div className={`border ${accent ? "border-[#E60000]/40" : "border-white/10"} bg-[#050505] p-5`}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">{label}</div>
      <div className={`font-display font-extrabold mt-1 ${big ? "text-3xl" : "text-2xl"} ${accent ? "text-[#E60000]" : muted ? "text-zinc-400" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

function KlarnaHint({ amount }) {
  const value = Number(amount || 0);
  if (!value || value <= 0) return null;
  if (value <= 1000) {
    const per = (value / 3).toFixed(2);
    return (
      <div
        data-testid="klarna-hint"
        className="mt-3 flex items-center gap-2 text-[11px] text-zinc-400 border-t border-white/10 pt-3"
      >
        <KlarnaPill />
        <span>
          or 3 interest-free payments of <span className="text-white font-bold">£{per}</span> with Klarna
        </span>
      </div>
    );
  }
  return (
    <div
      data-testid="klarna-hint"
      className="mt-3 flex items-center gap-2 text-[11px] text-zinc-400 border-t border-white/10 pt-3"
    >
      <KlarnaPill />
      <span>Split into flexible monthly payments with Klarna</span>
    </div>
  );
}

function KlarnaPill() {
  return (
    <span
      aria-label="Klarna"
      className="inline-flex items-center justify-center rounded-sm bg-[#FFA8CD] text-black text-[9px] font-black tracking-wide px-1.5 py-0.5 leading-none"
      style={{ fontFamily: "Helvetica, Arial, sans-serif" }}
    >
      Klarna.
    </span>
  );
}
